import React from 'react';
import clsx from 'clsx';

//lib
import { ORIENTATION_VERTICAL } from '@/lib/gridElements/orientation';
import { SIZE_25 } from '@/lib/gridElements/size';

//Component
import { KPI_VARIANTS, KPI_VARIANTS_DARK } from '@/components/base/Kpi';

const Wrapper = ({
    config,
    children,
    className,
    variant = 'default',
    isExport = false,
    ...restProps
}) => (
    <div
        className={clsx(
            'relative flex h-full w-full flex-col items-center',
            KPI_VARIANTS[variant],
            className,
            { [KPI_VARIANTS_DARK[variant]]: !isExport }
        )}
        {...restProps}>
        {children
            ? React.Children.map(children, (child) => {
                  const newChild = React.cloneElement(child, {
                      config
                  });
                  return newChild;
              })
            : null}
    </div>
);

const ContentWrapper = ({ children, className, ...restProps }) => {
    return (
        <div
            className={clsx(
                'flex h-full max-h-80 w-full flex-1 animate-pulse items-center justify-center',
                className
            )}
            {...restProps}>
            {children}
        </div>
    );
};

const IconLoader = ({ className, isExport = false }) => {
    return (
        <div className="flex items-center justify-center">
            <span
                className={clsx('block rounded-full bg-icoGray-300', className, {
                    'dark:bg-icoDarkMode-deepBlack': !isExport
                })}
            />
        </div>
    );
};

export const DefaultLoader = ({ config }) => {
    return (
        <Wrapper>
            <ContentWrapper
                className={clsx('px-8 py-12', {
                    'flex-col space-y-8': config.orientation === ORIENTATION_VERTICAL,
                    'flex-col space-x-0 space-y-8 lg:flex-row lg:space-x-8 lg:space-y-0':
                        config.orientation !== ORIENTATION_VERTICAL
                })}
                data-testid="skeleton-loading">
                <IconLoader className="h-17 w-17" />
                <div
                    //make sure placeholder is close to real kpi size to avoid content shift
                    className={clsx('flex flex-col space-y-[6px]', {
                        'w-3/4 items-center justify-center text-center':
                            config.orientation === ORIENTATION_VERTICAL,
                        'w-2/3': config.orientation !== ORIENTATION_VERTICAL,
                        '!w-3/4 items-center justify-center text-center lg:!w-2/3 lg:items-start lg:justify-start lg:text-left':
                            config.size !== SIZE_25
                    })}>
                    <span
                        className={clsx(
                            'block w-full rounded bg-icoGray-300 py-3 dark:bg-icoDarkMode-deepBlack'
                        )}
                    />
                    <span className="block w-full rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                    <span className="block w-full rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                    <span className="inline-block w-3/4 rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                </div>
            </ContentWrapper>
        </Wrapper>
    );
};

export const MediaViewerKpiLoader = () => {
    return (
        <Wrapper variant="noShadow">
            <ContentWrapper className="flex-col space-y-4 px-4 py-8">
                <IconLoader className="h-[3.125rem] w-[3.125rem]" />
                <div
                    //make sure placeholder is close to real kpi size to avoid content shift
                    className="flex w-3/4 flex-col items-center justify-center space-y-[6px] text-center">
                    <span
                        className={clsx(
                            'block w-full rounded bg-icoGray-300 py-3 dark:bg-icoDarkMode-deepBlack'
                        )}
                    />
                    <span className="block w-full rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                </div>
            </ContentWrapper>
        </Wrapper>
    );
};

export const IndustryBenchmarkLoader = ({ hasThirdLine, variant }) => (
    <Wrapper variant={variant}>
        <ContentWrapper className="flex-col space-y-4 px-4 py-8">
            <IconLoader className="h-17 w-17" />
            <div className="spacer-y-2 mt-5 w-full flex-1 text-center">
                <span className="inline-block w-3/4 rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                <span className="mt-2 inline-block w-1/2 rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                {hasThirdLine ? (
                    <span className="m-auto mt-2 block w-1/4 rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                ) : null}
            </div>
        </ContentWrapper>
    </Wrapper>
);

export const DashboardLoader = ({ config, isExport = false }) => (
    <Wrapper isExport={isExport}>
        <div className="flex w-full items-center justify-between py-5 pl-2 pr-4">
            <span
                className={clsx('h-7 w-7 rounded-full bg-icoGray-300', {
                    'dark:bg-icoDarkMode-deepBlack': !isExport
                })}
            />
            <div className="flex items-center space-x-0.5">
                <span
                    className={clsx('bg-icoGray-30 h-1 w-1 rounded-full', {
                        'dark:bg-icoDarkMode-deepBlack': !isExport
                    })}
                />
                <span
                    className={clsx('bg-icoGray-30 h-1 w-1 rounded-full', {
                        'dark:bg-icoDarkMode-deepBlack': !isExport
                    })}
                />
                <span
                    className={clsx('bg-icoGray-30 h-1 w-1 rounded-full', {
                        'dark:bg-icoDarkMode-deepBlack': !isExport
                    })}
                />
            </div>
        </div>
        <ContentWrapper
            className={clsx('px-8 pb-16', {
                'flex-col space-y-8': config.orientation === ORIENTATION_VERTICAL
            })}>
            <IconLoader className="h-17 w-17" isExport={isExport} />
            <div
                //make sure placeholder is close to real kpi size to avoid content shift
                className={clsx('flex flex-col space-y-[6px]', {
                    'w-3/4 items-center justify-center text-center':
                        config.orientation === ORIENTATION_VERTICAL
                })}>
                <span
                    className={clsx('block w-full rounded bg-icoGray-300 py-3', {
                        'dark:bg-icoDarkMode-deepBlack': !isExport
                    })}
                />
                <span
                    className={clsx('block w-full rounded bg-icoGray-300 py-2', {
                        'dark:bg-icoDarkMode-deepBlack': !isExport
                    })}
                />
                <span
                    className={clsx('block w-full rounded bg-icoGray-300 py-2', {
                        'dark:bg-icoDarkMode-deepBlack': !isExport
                    })}
                />
                <span
                    className={clsx('inline-block w-3/4 rounded bg-icoGray-300 py-2', {
                        'dark:bg-icoDarkMode-deepBlack': !isExport
                    })}
                />
            </div>
        </ContentWrapper>
    </Wrapper>
);
